<script setup>
/* global axios */
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CentertelNewConversation from './CentertelNewConversation.vue';

const store = useStore();
const currentUser = computed(() => store.getters.getCurrentUser);
const currentRole = computed(() => store.getters.getCurrentRole);
const customRoleId = computed(() => store.getters.getCurrentCustomRoleId);
const customRoles = computed(() => store.getters['customRole/getCustomRoles'] || []);
const customRoleName = computed(() => {
  if (!customRoleId.value) return null;
  const role = customRoles.value.find(r => r.id === customRoleId.value);
  return role?.name || null;
});
const isSupervisor = computed(() => currentRole.value === 'agent' && customRoleName.value === 'Supervisor');
const isVendor = computed(() => currentRole.value === 'agent' && !isSupervisor.value);
const isAdmin = computed(() => currentRole.value === 'administrator');

// Começa igual ao nativo do Chatwoot (open)
const defaultStatus = 'open';
const filters = ref({
  labels: [],
  inboxIds: [],
  teamIds: [],
  agentId: null,
  unreadOnly: false,
  status: defaultStatus,
  search: '',
});

const statusOptions = [
  { key: 'open',     label: 'Abertas'   },
  { key: 'pending',  label: 'Pendentes' },
  { key: 'resolved', label: 'Resolvidas'},
];

// Contadores reais via endpoint leve (1 query SQL, 1 request)
const statusCounts = ref({ open: 0, pending: 0, resolved: 0 });
const fetchCounts = async () => {
  try {
    const id = store.getters.getCurrentAccountId;
    const agentParam = isVendor.value ? `?agent_id=${currentUser.value?.id}` : '';
    const { data } = await axios.get(`/api/v1/accounts/${id}/centertel_stats${agentParam}`);
    statusCounts.value = data;
  } catch { /* ignore */ }
};


// Dropdowns
const activeDropdown = ref('');
const searchResults = ref([]);

// Data
const labels = computed(() => store.getters['labels/getLabels'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const agents = computed(() => store.getters['agents/getAgents'] || []);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
  if (!isVendor.value) store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
  fetchCounts();
  // Aplicar classe CSS por cargo quando custom roles carregarem
  const applyRoleClass = () => {
    document.body.classList.remove('ct-vendor', 'ct-supervisor');
    if (isSupervisor.value) {
      document.body.classList.add('ct-supervisor');
    } else if (isVendor.value) {
      document.body.classList.add('ct-vendor');
      applyFilters();
    }
  };
  // Tentar imediato (se roles já carregaram)
  setTimeout(applyRoleClass, 500);
  // Reagir quando customRoleName mudar (API respondeu)
  watch(customRoleName, applyRoleClass);
});

const toggleDropdown = (name) => {
  activeDropdown.value = activeDropdown.value === name ? '' : name;
};

const toggleFilter = (type, id) => {
  const arr = filters.value[type];
  const idx = arr.indexOf(id);
  if (idx >= 0) arr.splice(idx, 1);
  else arr.push(id);
};

const setAgent = (id) => {
  filters.value.agentId = filters.value.agentId === id ? null : id;
};

const applyAndClose = () => {
  activeDropdown.value = '';
  applyFilters();
};

const toggleUnread = () => {
  filters.value.unreadOnly = !filters.value.unreadOnly;
  applyFilters();
};

const setStatus = (key) => {
  filters.value.status = key;
  applyFilters();
};

let searchDebounce = null;
const applyFilters = () => {
  if (window.__centertelApplyFilters) {
    // Vendedor: em abertas/resolvidas, filtrar só as próprias conversas
    // Em pendentes, ver todas (fila de leads sem dono)
    const status = filters.value.status;
    const autoAgentId = isVendor.value && status !== 'pending'
      ? currentUser.value?.id
      : filters.value.agentId || undefined;

    window.__centertelApplyFilters({
      status,
      labels: filters.value.labels.length ? filters.value.labels : undefined,
      inboxIds: filters.value.inboxIds.length ? filters.value.inboxIds : undefined,
      teamIds: filters.value.teamIds.length ? filters.value.teamIds : undefined,
      agentId: autoAgentId,
      q: filters.value.search || undefined,
      sortBy: filters.value.unreadOnly ? 'unread' : undefined,
    });
  } else if (window.__centertelChangeStatus) {
    // fallback legacy
    window.__centertelChangeStatus(filters.value.status);
  }
  fetchCounts();
};

const applySearch = () => {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(() => applyFilters(), 350);
};

const clearAllFilters = () => {
  filters.value = {
    labels: [],
    inboxIds: [],
    teamIds: [],
    agentId: null,
    unreadOnly: false,
    status: 'open',
    search: '',
  };
  applyFilters();
};

const hasActiveFilters = computed(() => {
  return filters.value.labels.length > 0 ||
    filters.value.inboxIds.length > 0 ||
    filters.value.teamIds.length > 0 ||
    filters.value.agentId ||
    filters.value.unreadOnly;
});

// Nova conversa
const showNewConv = ref(false);
</script>

<template>
  <div class="centertel-toolbar">
    <!-- Row 0: Status (Abertas / Pendentes / Resolvidas) -->
    <div class="ct-status-row">
      <button
        v-for="opt in statusOptions"
        :key="opt.key"
        class="ct-status-btn"
        :class="{ active: filters.status === opt.key }"
        @click="setStatus(opt.key)"
      >
        {{ opt.label }}
        <span v-if="statusCounts[opt.key] > 0" class="ct-count">{{ statusCounts[opt.key] }}</span>
      </button>
    </div>

    <!-- Row 1: Botões de filtro -->
    <div class="ct-toolbar-row">
      <!-- + Novo -->
      <button
        class="ct-fbtn ct-fbtn-novo"
        title="Nova conversa"
        @click="showNewConv = true"
      >
        + Novo
      </button>

      <!-- Tags -->
      <div class="relative">
        <button
          class="ct-fbtn"
          :class="{ active: filters.labels.length > 0 }"
          title="Tags"
          @click="toggleDropdown('tags')"
        >
          🏷️
          <span v-if="filters.labels.length" class="ct-badge">{{ filters.labels.length }}</span>
        </button>
        <div v-if="activeDropdown === 'tags'" v-on-clickaway="() => activeDropdown = ''" class="ct-dropdown">
          <div class="ct-drop-list">
            <label
              v-for="label in labels"
              :key="label.id"
              class="ct-drop-item"
              @click.prevent="toggleFilter('labels', label.title)"
            >
              <input type="checkbox" :checked="filters.labels.includes(label.title)" />
              <span class="ct-dot" :style="{ background: label.color }" />
              <span>{{ label.title }}</span>
            </label>
          </div>
          <button class="ct-drop-apply" @click="applyAndClose">Aplicar</button>
        </div>
      </div>

      <!-- Conexões (Inbox) -->
      <div class="relative">
        <button
          class="ct-fbtn"
          :class="{ active: filters.inboxIds.length > 0 }"
          title="Conexões"
          @click="toggleDropdown('inbox')"
        >
          📥
          <span v-if="filters.inboxIds.length" class="ct-badge">{{ filters.inboxIds.length }}</span>
        </button>
        <div v-if="activeDropdown === 'inbox'" v-on-clickaway="() => activeDropdown = ''" class="ct-dropdown">
          <div class="ct-drop-list">
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="ct-drop-item"
              @click.prevent="toggleFilter('inboxIds', inbox.id)"
            >
              <input type="checkbox" :checked="filters.inboxIds.includes(inbox.id)" />
              <span>{{ inbox.name }}</span>
            </label>
          </div>
          <button class="ct-drop-apply" @click="applyAndClose">Aplicar</button>
        </div>
      </div>

      <!-- Times -->
      <div class="relative">
        <button
          class="ct-fbtn"
          :class="{ active: filters.teamIds.length > 0 }"
          title="Times"
          @click="toggleDropdown('teams')"
        >
          🏢
          <span v-if="filters.teamIds.length" class="ct-badge">{{ filters.teamIds.length }}</span>
        </button>
        <div v-if="activeDropdown === 'teams'" v-on-clickaway="() => activeDropdown = ''" class="ct-dropdown">
          <div class="ct-drop-list">
            <label
              v-for="team in teams"
              :key="team.id"
              class="ct-drop-item"
              @click.prevent="toggleFilter('teamIds', team.id)"
            >
              <input type="checkbox" :checked="filters.teamIds.includes(team.id)" />
              <span>{{ team.name }}</span>
            </label>
          </div>
          <button class="ct-drop-apply" @click="applyAndClose">Aplicar</button>
        </div>
      </div>

      <!-- Agentes (só admin/supervisor) -->
      <div v-if="!isVendor" class="relative">
        <button
          class="ct-fbtn"
          :class="{ active: !!filters.agentId }"
          title="Agentes"
          @click="toggleDropdown('agents')"
        >
          👤
        </button>
        <div v-if="activeDropdown === 'agents'" v-on-clickaway="() => activeDropdown = ''" class="ct-dropdown">
          <div class="ct-drop-list">
            <div
              class="ct-drop-item"
              @click="setAgent(null)"
            >
              <input type="radio" :checked="!filters.agentId" />
              <span>Todos</span>
            </div>
            <div
              v-for="agent in agents"
              :key="agent.id"
              class="ct-drop-item"
              @click="setAgent(agent.id)"
            >
              <input type="radio" :checked="filters.agentId === agent.id" />
              <span>{{ agent.name }}</span>
            </div>
          </div>
          <button class="ct-drop-apply" @click="applyAndClose">Aplicar</button>
        </div>
      </div>

      <!-- Não lidas -->
      <button
        class="ct-fbtn"
        :class="{ active: filters.unreadOnly }"
        title="Não lidas"
        @click="toggleUnread"
      >
        🔔
      </button>

      <!-- Limpar filtros -->
      <button
        v-if="hasActiveFilters"
        class="ct-fbtn ct-fbtn-clear"
        title="Limpar filtros"
        @click="clearAllFilters"
      >
        ✕
      </button>
    </div>

    <!-- Row 2: Busca -->
    <div class="ct-toolbar-search">
      <span class="ct-search-icon">🔍</span>
      <input
        v-model="filters.search"
        type="text"
        placeholder="Buscar conversas..."
        class="ct-search-input"
        @input="applySearch"
      />
    </div>
    <!-- Modal Nova Conversa -->
    <CentertelNewConversation v-if="showNewConv" @close="showNewConv = false" />
  </div>
</template>

<style scoped>
.centertel-toolbar {
  padding: 6px 8px;
  border-bottom: 1px solid var(--n-border, #e5e7eb);
  background: var(--n-bg, #fff);
}

/* Status row */
.ct-status-row {
  display: flex;
  gap: 2px;
  margin-bottom: 6px;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 2px;
}
.ct-status-btn {
  flex: 1;
  padding: 4px 0;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s;
}
.ct-status-btn:hover { color: #374151; background: rgba(255,255,255,0.7); }
.ct-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 9px; font-size: 10px; font-weight: 700;
  background: #e5e7eb; color: #374151; margin-left: 4px;
}
.ct-status-btn.active .ct-count {
  background: #fbbf24; color: #78350f;
}
.ct-status-btn.active {
  background: #fff;
  color: #d97706;
  font-weight: 700;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.ct-toolbar-row {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 6px;
}

.ct-fbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
  position: relative;
  min-height: 28px;
}
.ct-fbtn:hover { background: #fef3c7; border-color: #f59e0b; }
.ct-fbtn.active { background: #fef3c7; border-color: #f59e0b; color: #92400e; }

.ct-fbtn-novo {
  background: #f59e0b !important;
  color: #fff !important;
  border-color: #f59e0b !important;
  font-weight: 600;
}
.ct-fbtn-novo:hover { background: #d97706 !important; }

.ct-fbtn-clear {
  color: #dc2626;
  border-color: #fecaca;
  font-weight: 600;
  font-size: 10px;
  padding: 4px 6px;
}

.ct-badge {
  background: #f59e0b;
  color: #fff;
  border-radius: 50%;
  font-size: 9px;
  font-weight: 700;
  min-width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -4px;
  right: -4px;
}

.ct-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  z-index: 9999;
  min-width: 180px;
  display: flex;
  flex-direction: column;
}

.ct-drop-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 4px;
}

.ct-drop-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.1s;
}
.ct-drop-item:hover { background: #f9fafb; }
.ct-drop-item input[type="checkbox"],
.ct-drop-item input[type="radio"] { width: 14px; height: 14px; accent-color: #f59e0b; pointer-events: none; }

.ct-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.ct-drop-apply {
  display: block;
  width: calc(100% - 8px);
  padding: 6px;
  margin: 4px;
  border: none;
  border-radius: 6px;
  border-top: 1px solid #e5e7eb;
  background: #f59e0b;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
}
.ct-drop-apply:hover { background: #d97706; }

.ct-toolbar-search {
  position: relative;
}

.ct-search-icon {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
}

.ct-search-input {
  width: 100%;
  padding: 5px 8px 5px 28px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 12px;
  background: #f9fafb;
  outline: none;
}
.ct-search-input:focus { border-color: #f59e0b; }

/* Vendedor: regras em centertel-sidebar.css */
</style>
